import React, { ComponentPropsWithRef } from 'react'
import { ScaleItem } from './types'
import { useTranslation } from '../../i18n/useTranslation'
import { useCommonCtx } from '../../context/useCommonCtx'
import { AnswserScoreType, NO_SCORE } from '../../services/types'
import { Box, Slider, Typography } from '@mui/material'
import { twMerge } from 'tailwind-merge'
import { Icons } from '../../assets/icons'
import { TKeys } from '../../i18n/types'

interface Props extends ComponentPropsWithRef<'div'> {
    onPickScale: (scale: AnswserScoreType['Score']) => void
    noScoreOption?: ScaleItem
    options: ScaleItem[]
    marksValues?: number[] //FIXME: should be AnswserScoreType['Score'][]
    scaleValue: AnswserScoreType['Score']
    minValue?: AnswserScoreType['Score']
    maxValue?: AnswserScoreType['Score']
    avgValue?: AnswserScoreType['Score']
    withEmoji?: boolean
    withEndScale?: boolean
    withCustomMarksLabels?: boolean
    children?: React.ReactNode // for layout purpose
}

export const ScaleSlider = (p: Props) => {
    const {
        className,
        onPickScale,
        children = '',
        scaleValue,
        options,
        noScoreOption = { key: 'lvl0' as TKeys, value: NO_SCORE },
        marksValues = options.map((o) => o.value),
        minValue = options[0].value,
        maxValue = options[options.length - 1].value,
        avgValue = options[Math.floor(options.length / 2) as number].value,
        withEmoji = false,
        withEndScale = false,
        withCustomMarksLabels = true,
        ...rest
    } = p
    const t = useTranslation(useCommonCtx());
    const option = options.find((o) => o.value === scaleValue) ?? noScoreOption
    const marks = options.map((o) => ({
        value: o.value,
        label: marksValues.includes(o.value)
            ? withCustomMarksLabels
                ? t(o.key)
                : o.value
            : '',
    }))
    return (
        <div
            className={twMerge(
                'w-screen sm:w-full px-8 py-4 flex flex-col justify-center items-center gap-8',
                className
            )}
            {...rest}
        >
            <Box className="flex-[1.5] w-full">
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        direction: 'ltr', // arabic or not, keep this direction here
                    }}
                >
                    {/* FIXME no generic */}
                    {withEndScale && (
                        <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            {/* {minValue} */}
                            {t('lvlSC10x0nps').toUpperCase()}
                        </Typography>
                    )}
                    {!withEndScale && (
                        <div className="flex flex-row items-center opacity-80 hover:opacity-100">
                            <Icons.ArrowBack
                                sx={{ fontSize: '4rem' }}
                                fontSize="large"
                            />
                            <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                {t('lvl2').toUpperCase()}
                            </Typography>
                        </div>
                    )}
                    {withEndScale && (
                        <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            {/* {maxValue} */}
                            {t('lvlSC10x10nps').toUpperCase()}
                        </Typography>
                    )}
                    {!withEndScale && (
                        <div className="flex flex-row items-center opacity-80 hover:opacity-100">
                            <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                {t('lvl6').toUpperCase()}
                            </Typography>{' '}
                            <Icons.ArrowForward
                                sx={{ fontSize: '4rem' }}
                                fontSize="large"
                            />
                        </div>
                    )}
                </Box>
                <Slider
                    sx={{
                        color:
                            option.value === noScoreOption.value
                                ? 'lightgray'
                                : 'primary',
                    }}
                    // className="flex-[1.5]"
                    // sx={{
                    // '& input[type="range"]': {
                    //     WebkitAppearance: 'slider-vertical',
                    // },
                    // }}
                    // orientation="vertical"
                    size="medium"
                    value={
                        option.value === noScoreOption.value
                            ? avgValue
                            : option.value
                    }
                    step={1}
                    marks={marks}
                    min={minValue}
                    max={maxValue}
                    onChangeCommitted={(_, v) => {
                        // NB onChange={(_, v) is not firing when clicking on current value
                        console.log('onChangeCommitted', v)
                        onPickScale(v as AnswserScoreType['Score'])
                    }}
                    onChange={(_, v) => {
                        console.log('onChange', v)
                        onPickScale(v as AnswserScoreType['Score'])
                    }}
                />
            </Box>

            <div className="flex flex-1 self-stretch flex-row justify-between items-center ">
                {withEmoji && (
                    <div
                        className={`flex flex-[4] self-stretch flex-col items-center justify-center rounded-full m-2 p-2 border-2 border-gray-300 text-center ${
                            scaleValue === option.value
                                ? 'bg-gray-300'
                                : 'bg-white'
                        }`}
                        onClick={() =>
                            onPickScale(
                                option.value as AnswserScoreType['Score']
                            )
                        }
                    >
                        <span
                            children={t((option.key + 'e') as TKeys)}
                            className="text-4xl"
                        />
                        {/* <span
                            children={t(option.key)}
                            className="text-lg line-clamp-1"
                        /> */}
                    </div>
                )}
                {children && (
                    <div className="flex flex-1 justify-center mt-4">
                        {children}
                    </div>
                )}
            </div>
        </div>
    )
}
